import { Cell, Grid, HeadingSection, InfoLabel, Text } from 'bold-ui'
import { booleanToSimNaoTitleCase } from 'components/form'
import { CondicoesSaudeAutorreferidas } from 'graphql/types.generated'
import React from 'react'
import { isObjectDeepEmpty } from 'util/object'
import {
  doencaCardiacaAutorreferidoRecord,
  doencaRespiratoriaAutorreferidoRecord,
  doencaRinsAutorreferidoRecord,
  pesoAutorreferidoRecord,
} from 'view/cidadao/createfci/model-cidadaoFci'
import { formatSimNaoJoinRespostas } from 'view/cidadao/util-cidadao'

interface CidadaoFciCondicoesSaudeAutorreferidasSectionProps {
  condicoesSaudeAutorreferidas: CondicoesSaudeAutorreferidas
}

export const CidadaoFciCondicoesSaudeAutorreferidasSection = (
  props: CidadaoFciCondicoesSaudeAutorreferidasSectionProps
) => {
  const { condicoesSaudeAutorreferidas } = props

  const { id, __typename, ...rest } = condicoesSaudeAutorreferidas ?? {}
  const isCondicoesSaudeAutorreferidasVazio = isObjectDeepEmpty(rest)

  if (isCondicoesSaudeAutorreferidasVazio)
    return (
      <HeadingSection level={2} title='Condições/situações de saúde autorreferidas'>
        <Text fontStyle='italic'>Sem informações registradas</Text>
      </HeadingSection>
    )

  return (
    <HeadingSection level={2} title='Condições/situações de saúde autorreferidas'>
      <Grid wrap>
        <Cell size={4}>
          <InfoLabel title='Gestante'>{booleanToSimNaoTitleCase(condicoesSaudeAutorreferidas?.stGestante)}</InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Maternidade de referência'>{condicoesSaudeAutorreferidas?.maternidadeReferencia}</InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Peso autoreferido'>
            {pesoAutorreferidoRecord[condicoesSaudeAutorreferidas?.autodenominacaoPeso]}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Acamado'>{booleanToSimNaoTitleCase(condicoesSaudeAutorreferidas?.stAcamado)}</InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Domiciliado'>
            {booleanToSimNaoTitleCase(condicoesSaudeAutorreferidas?.stDomiciliar)}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Usa outras práticas integrativas e complementares'>
            {booleanToSimNaoTitleCase(condicoesSaudeAutorreferidas?.stPraticasComplementares)}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Fumante'>{booleanToSimNaoTitleCase(condicoesSaudeAutorreferidas?.stFumante)}</InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Usa álcool'>
            {booleanToSimNaoTitleCase(condicoesSaudeAutorreferidas?.stUsoAlcool)}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Usa outras drogas'>
            {booleanToSimNaoTitleCase(condicoesSaudeAutorreferidas?.stUsoOutrasDrogas)}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Teve diagnóstico de problema de saúde mental'>
            {booleanToSimNaoTitleCase(condicoesSaudeAutorreferidas?.stProblemaSaudeMental)}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Tem hipertensão arterial'>
            {booleanToSimNaoTitleCase(condicoesSaudeAutorreferidas?.stHipertensaoArterial)}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Tem diabetes'>
            {booleanToSimNaoTitleCase(condicoesSaudeAutorreferidas?.stDiabetes)}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Tem/teve câncer'>
            {booleanToSimNaoTitleCase(condicoesSaudeAutorreferidas?.stCancer)}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Teve AVC/derrame'>
            {booleanToSimNaoTitleCase(condicoesSaudeAutorreferidas?.stDerrame)}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Teve infarto'>
            {booleanToSimNaoTitleCase(condicoesSaudeAutorreferidas?.stInfarto)}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Está com hanseníase'>
            {booleanToSimNaoTitleCase(condicoesSaudeAutorreferidas?.stHanseniase)}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Está com tuberculose'>
            {booleanToSimNaoTitleCase(condicoesSaudeAutorreferidas?.stTuberculose)}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Teve doença cardíaca/do coração'>
            {formatSimNaoJoinRespostas(
              condicoesSaudeAutorreferidas?.stDoencaCardiaca,
              condicoesSaudeAutorreferidas?.doencaCardiaca.map((item) => doencaCardiacaAutorreferidoRecord[item])
            )}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Teve doença respiratória/no pulmão'>
            {formatSimNaoJoinRespostas(
              condicoesSaudeAutorreferidas?.stDoencaRespiratoria,
              condicoesSaudeAutorreferidas?.doencasRespiratorias.map(
                (item) => doencaRespiratoriaAutorreferidoRecord[item]
              )
            )}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Tem/teve problema nos rins'>
            {formatSimNaoJoinRespostas(
              condicoesSaudeAutorreferidas?.stProblemaRins,
              condicoesSaudeAutorreferidas?.doencaRins.map((item) => doencaRinsAutorreferidoRecord[item])
            )}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Internação nos últimos 12 meses'>
            {formatSimNaoJoinRespostas(
              condicoesSaudeAutorreferidas?.stInternacaoUltimos12Meses,
              condicoesSaudeAutorreferidas?.causaInternacaoUltimos12Meses
            )}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Usa plantas medicinais'>
            {booleanToSimNaoTitleCase(condicoesSaudeAutorreferidas?.stPlantasMedicinais)}
          </InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Outra condição de saúde'>{condicoesSaudeAutorreferidas?.condicaoSaude1}</InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Outra condição de saúde'>{condicoesSaudeAutorreferidas?.condicaoSaude2}</InfoLabel>
        </Cell>
        <Cell size={4}>
          <InfoLabel title='Outra condição de saúde'>{condicoesSaudeAutorreferidas?.condicaoSaude3}</InfoLabel>
        </Cell>
      </Grid>
    </HeadingSection>
  )
}
